/**
 * Budget API Service - RTK Query
 * BuilderBel Construction Project Management System
 */

import { baseApi, ApiResponse } from './api';
import type {
  Budget,
  BudgetLineItem,
  BudgetTransfer,
  BudgetSummary,
  VarianceReport,
  CashFlowDataPoint,
  CreateBudgetRequest,
  UpdateBudgetRequest,
  CreateLineItemRequest,
  UpdateLineItemRequest,
  CreateTransferRequest,
  UpdateForecastRequest,
  ExportBudgetRequest,
  BudgetFilterParams,
} from '../types/budget';

/**
 * Budget API Endpoints
 */
export const budgetApi = baseApi.injectEndpoints({
  endpoints: builder => ({
    // Get project budget
    getBudget: builder.query<ApiResponse<Budget>, { projectId: string }>({
      query: ({ projectId }) => ({
        url: `/projects/${projectId}/budget`,
      }),
      providesTags: ['Budget'],
    }),

    // Create budget
    createBudget: builder.mutation<ApiResponse<Budget>, CreateBudgetRequest>({
      query: data => ({
        url: `/projects/${data.projectId}/budget`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Budget'],
    }),

    // Update budget
    updateBudget: builder.mutation<
      ApiResponse<Budget>,
      { projectId: string; budgetId: string; data: UpdateBudgetRequest }
    >({
      query: ({ projectId, budgetId, data }) => ({
        url: `/projects/${projectId}/budget/${budgetId}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['Budget'],
    }),

    // Delete budget
    deleteBudget: builder.mutation<ApiResponse<void>, { projectId: string; budgetId: string }>({
      query: ({ projectId, budgetId }) => ({
        url: `/projects/${projectId}/budget/${budgetId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Budget'],
    }),

    // Get budget line items
    getLineItems: builder.query<
      ApiResponse<BudgetLineItem[]>,
      { projectId: string; budgetId: string; filters?: BudgetFilterParams }
    >({
      query: ({ projectId, budgetId, filters }) => ({
        url: `/projects/${projectId}/budget/${budgetId}/line-items`,
        params: filters,
      }),
      providesTags: ['Budget'],
    }),

    // Create line item
    createLineItem: builder.mutation<
      ApiResponse<BudgetLineItem>,
      { projectId: string; budgetId: string; data: CreateLineItemRequest }
    >({
      query: ({ projectId, budgetId, data }) => ({
        url: `/projects/${projectId}/budget/${budgetId}/line-items`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Budget'],
    }),

    // Update line item
    updateLineItem: builder.mutation<
      ApiResponse<BudgetLineItem>,
      { projectId: string; budgetId: string; lineItemId: string; data: UpdateLineItemRequest }
    >({
      query: ({ projectId, budgetId, lineItemId, data }) => ({
        url: `/projects/${projectId}/budget/${budgetId}/line-items/${lineItemId}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['Budget'],
    }),

    // Delete line item
    deleteLineItem: builder.mutation<
      ApiResponse<void>,
      { projectId: string; budgetId: string; lineItemId: string }
    >({
      query: ({ projectId, budgetId, lineItemId }) => ({
        url: `/projects/${projectId}/budget/${budgetId}/line-items/${lineItemId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Budget'],
    }),

    // Get budget transfers
    getTransfers: builder.query<
      ApiResponse<BudgetTransfer[]>,
      { projectId: string; budgetId: string }
    >({
      query: ({ projectId, budgetId }) => ({
        url: `/projects/${projectId}/budget/${budgetId}/transfers`,
      }),
      providesTags: ['Budget'],
    }),

    // Create transfer request
    createTransfer: builder.mutation<
      ApiResponse<BudgetTransfer>,
      { projectId: string; budgetId: string; data: CreateTransferRequest }
    >({
      query: ({ projectId, budgetId, data }) => ({
        url: `/projects/${projectId}/budget/${budgetId}/transfers`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Budget'],
    }),

    // Approve transfer
    approveTransfer: builder.mutation<
      ApiResponse<BudgetTransfer>,
      { projectId: string; budgetId: string; transferId: string; notes?: string }
    >({
      query: ({ projectId, budgetId, transferId, notes }) => ({
        url: `/projects/${projectId}/budget/${budgetId}/transfers/${transferId}/approve`,
        method: 'POST',
        body: { notes },
      }),
      invalidatesTags: ['Budget'],
    }),

    // Reject transfer
    rejectTransfer: builder.mutation<
      ApiResponse<BudgetTransfer>,
      { projectId: string; budgetId: string; transferId: string; notes: string }
    >({
      query: ({ projectId, budgetId, transferId, notes }) => ({
        url: `/projects/${projectId}/budget/${budgetId}/transfers/${transferId}/reject`,
        method: 'POST',
        body: { notes },
      }),
      invalidatesTags: ['Budget'],
    }),

    // Update forecast
    updateForecast: builder.mutation<
      ApiResponse<BudgetLineItem>,
      { projectId: string; budgetId: string; data: UpdateForecastRequest }
    >({
      query: ({ projectId, budgetId, data }) => ({
        url: `/projects/${projectId}/budget/${budgetId}/forecast`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['Budget'],
    }),

    // Get budget summary
    getBudgetSummary: builder.query<
      ApiResponse<BudgetSummary>,
      { projectId: string; budgetId: string }
    >({
      query: ({ projectId, budgetId }) => ({
        url: `/projects/${projectId}/budget/${budgetId}/summary`,
      }),
      providesTags: ['Budget'],
    }),

    // Get variance report
    getVarianceReport: builder.query<
      ApiResponse<VarianceReport[]>,
      { projectId: string; budgetId: string; threshold?: number }
    >({
      query: ({ projectId, budgetId, threshold }) => ({
        url: `/projects/${projectId}/budget/${budgetId}/variance-report`,
        params: { threshold },
      }),
      providesTags: ['Budget'],
    }),

    // Get cash flow
    getCashFlow: builder.query<
      ApiResponse<CashFlowDataPoint[]>,
      { projectId: string; budgetId: string; startDate?: string; endDate?: string }
    >({
      query: ({ projectId, budgetId, startDate, endDate }) => ({
        url: `/projects/${projectId}/budget/${budgetId}/cash-flow`,
        params: { startDate, endDate },
      }),
      providesTags: ['Budget'],
    }),

    // Export budget
    exportBudget: builder.mutation<Blob, { projectId: string; data: ExportBudgetRequest }>({
      query: ({ projectId, data }) => ({
        url: `/projects/${projectId}/budget/${data.budgetId}/export`,
        method: 'POST',
        body: data,
        responseHandler: response => response.blob(),
      }),
    }),
  }),
});

/**
 * Export hooks for use in components
 */
export const {
  useGetBudgetQuery,
  useCreateBudgetMutation,
  useUpdateBudgetMutation,
  useDeleteBudgetMutation,
  useGetLineItemsQuery,
  useCreateLineItemMutation,
  useUpdateLineItemMutation,
  useDeleteLineItemMutation,
  useGetTransfersQuery,
  useCreateTransferMutation,
  useApproveTransferMutation,
  useRejectTransferMutation,
  useUpdateForecastMutation,
  useGetBudgetSummaryQuery,
  useGetVarianceReportQuery,
  useGetCashFlowQuery,
  useExportBudgetMutation,
} = budgetApi;
